import SpeakButton from "@/components/SpeakButton";
import { useCallback, useEffect, useRef, useState } from "react";
import { Star, ArrowLeft, ArrowRight, Play, Timer } from "lucide-react";
import { useT } from "@/i18n";
import { GAME_TEXT } from "@/components/games/content";

const DURATION = 30;
let audioCtx = null;

const playSparkle = () => {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const now = audioCtx.currentTime;
    [1568, 2093, 2637, 3136].forEach((f, i) => {
      const t = now + i * 0.05;
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(f, t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(t);
      osc.stop(t + 0.4);
    });
  } catch { /* audio not supported */ }
};

export default function StarCatcher() {
  const { lang } = useT();
  const tx = GAME_TEXT[lang];
  const [lumiX, setLumiX] = useState(45);
  const [stars, setStars] = useState([]);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(DURATION);
  const [state, setState] = useState("idle");
  const xRef = useRef(45);
  const stageRef = useRef(null);

  const moveTo = useCallback((x) => {
    xRef.current = Math.max(0, Math.min(90, x));
    setLumiX(xRef.current);
  }, []);

  const start = () => { audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)(); audioCtx.resume?.(); setScore(0); setStars([]); setTime(DURATION); setState("playing"); };

  useEffect(() => {
    if (state !== "playing") return;
    const tick = setInterval(() => {
      setStars((list) => {
        let caught = 0;
        const next = list
          .map((s) => ({ ...s, y: s.y + s.speed }))
          .filter((s) => {
            if (s.y >= 66 && s.y <= 92 && Math.abs(s.x + 2 - (xRef.current + 5)) < 9) { caught += 1; return false; }
            return s.y < 100;
          });
        if (caught) setScore((n) => n + caught);
        if (Math.random() < 0.06) next.push({ id: Math.random(), x: 3 + Math.random() * 90, y: -5, speed: 1 + Math.random() * 1.2 });
        return next;
      });
    }, 50);
    const clock = setInterval(() => setTime((t) => t - 1), 1000);
    return () => { clearInterval(tick); clearInterval(clock); };
  }, [state]);

  useEffect(() => { if (state === "playing" && time <= 0) setState("over"); }, [time, state]);

  const prevScore = useRef(0);
  useEffect(() => {
    if (score > prevScore.current) playSparkle();
    prevScore.current = score;
  }, [score]);

  useEffect(() => {
    const onKey = (e) => {
      if (state !== "playing") return;
      if (e.key === "ArrowLeft") { e.preventDefault(); moveTo(xRef.current - 6); }
      if (e.key === "ArrowRight") { e.preventDefault(); moveTo(xRef.current + 6); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, moveTo]);

  const onPointer = (e) => {
    if (state !== "playing" || !stageRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    moveTo(((e.clientX - r.left) / r.width) * 100 - 5);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2"><h3 className="font-fredoka text-xl sm:text-2xl font-bold text-blue-950">{tx.game}</h3><SpeakButton compact mascot="lumi" text={`${tx.game} ${tx.gameSub}`} testId="ask-games-stars" /></div>
      <p className="text-blue-900 font-semibold">{tx.gameSub}</p>
      <div ref={stageRef} onPointerMove={onPointer} onPointerDown={onPointer} className="mt-4 relative bg-gradient-to-b from-indigo-900 via-blue-800 to-sky-500 rounded-3xl border-4 border-white shadow-lg h-80 sm:h-96 overflow-hidden touch-none select-none" data-testid="star-game">
        <div className="absolute top-3 left-3 right-3 flex justify-between z-10">
          <span className="bg-white/90 rounded-full px-4 py-1 font-bold text-blue-950 inline-flex items-center gap-1" data-testid="star-score"><Star className="w-4 h-4 text-yellow-500 fill-yellow-400" /> {tx.score}: {score}</span>
          <span className="bg-white/90 rounded-full px-4 py-1 font-bold text-blue-950 inline-flex items-center gap-1" data-testid="star-time"><Timer className="w-4 h-4" /> {tx.time}: {Math.max(0, time)}s</span>
        </div>
        {stars.map((s) => (
          <Star key={s.id} className="absolute w-8 h-8 text-yellow-300 fill-yellow-300 drop-shadow" style={{ left: `${s.x}%`, top: `${s.y}%` }} />
        ))}
        <img src="/img/lumi.webp" alt="Lumi" className="absolute bottom-2 w-[10%] min-w-[48px] lumi-glow" style={{ left: `${lumiX}%` }} draggable="false" />
        {state !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-blue-950/40">
            {state === "over" && <p className="font-fredoka text-3xl font-bold bubble-text text-yellow-300" data-testid="star-final">{tx.final.replace("{n}", score)}</p>}
            <button type="button" onClick={start} className="glossy-btn bg-yellow-300 text-blue-900 font-fredoka font-bold text-2xl px-10 py-3 rounded-full inline-flex items-center gap-2" data-testid="star-play-btn">
              <Play className="w-6 h-6" /> {state === "over" ? tx.again : tx.play}
            </button>
          </div>
        )}
      </div>
      <div className="mt-3 flex justify-center gap-4 sm:hidden">
        <button type="button" onClick={() => moveTo(xRef.current - 10)} className="bg-white rounded-full p-4 shadow" aria-label="left" data-testid="star-left-btn"><ArrowLeft className="w-6 h-6" /></button>
        <button type="button" onClick={() => moveTo(xRef.current + 10)} className="bg-white rounded-full p-4 shadow" aria-label="right" data-testid="star-right-btn"><ArrowRight className="w-6 h-6" /></button>
      </div>
    </div>
  );
}
